import {
  SnapshotChannel,
  ObservableStatus,
  acknowledgeWithin,
  SyncRequest,
} from "@client/realtime/snapshot-channel";
import { io, type Socket } from "socket.io-client";

import { clearInvalidRecoveryCredential, shouldRequestSync } from "@client/realtime/recovery";
import type {
  ClientToServerEvents,
  CommandAck,
  GameCommand,
  ServerToClientEvents,
  StateSnapshot,
  PlayerProjection,
  SyncAck,
} from "@saboteur/contracts";

type PlayerSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
type SnapshotListener = (snapshot: Extract<StateSnapshot, { audience: "player" }>) => void;
export type ConnectionStatus = "connecting" | "online" | "offline" | "invalid" | "expired";

export interface PlayerSocketOptions {
  roomCode: string;
  participantToken: string;
  acknowledgementTimeoutMs?: number;
  socket?: PlayerSocket;
}

export class PlayerGameSocket {
  readonly #socket: PlayerSocket;
  readonly #snapshots = new SnapshotChannel("player");
  readonly #syncRequest = new SyncRequest();
  readonly #status = new ObservableStatus<ConnectionStatus>("connecting");
  readonly #acknowledgementTimeoutMs: number;
  readonly #roomCode: string;

  public constructor(options: PlayerSocketOptions) {
    this.#acknowledgementTimeoutMs = options.acknowledgementTimeoutMs ?? 5_000;
    this.#roomCode = options.roomCode;
    this.#socket =
      options.socket ??
      io({
        autoConnect: false,
        auth: {
          audience: "player",
          roomCode: options.roomCode,
          participantToken: options.participantToken,
        },
        reconnection: true,
      });
    this.#socket.on("state:snapshot", (snapshot) => {
      if (this.#snapshots.accept(snapshot) === "gap") void this.sync();
    });
    this.#socket.on("connect", () => {
      this.notifyStatus("online");
      if (
        shouldRequestSync({
          recovered: this.#socket.recovered,
          currentVersion: this.#snapshots.version,
        })
      ) {
        void this.sync();
      }
    });
    this.#socket.on("disconnect", () => {
      this.#syncRequest.invalidate();
      this.notifyStatus("offline");
    });
    this.#socket.on("connect_error", (error) => {
      const message = error.message.toUpperCase();
      if (message.includes("EXPIRED")) {
        clearInvalidRecoveryCredential(this.#roomCode, "ROOM_EXPIRED");
        this.notifyStatus("expired");
      } else if (message.includes("UNAUTHORIZED") || message.includes("NOT_FOUND")) {
        clearInvalidRecoveryCredential(this.#roomCode, "UNAUTHORIZED");
        this.notifyStatus("invalid");
      } else {
        this.notifyStatus("offline");
      }
    });
    this.#socket.on("room:expired", ({ code }) => {
      clearInvalidRecoveryCredential(this.#roomCode, code);
      this.notifyStatus("expired");
    });
    this.#socket.on("presence:update", (presence) => {
      if (!this.#snapshots.mergePresence(presence)) void this.sync();
    });
  }

  public connect(): void {
    this.notifyStatus("connecting");
    this.#socket.connect();
  }

  public disconnect(): void {
    this.#socket.disconnect();
  }

  public subscribe(listener: SnapshotListener): () => void {
    return this.#snapshots.subscribe(listener);
  }

  public subscribeStatus(listener: (status: ConnectionStatus) => void): () => void {
    return this.#status.subscribe(listener);
  }

  public acceptBootstrap(state: PlayerProjection): void {
    this.#snapshots.accept({ audience: "player", version: state.public.version, state }, true);
  }

  public async command(command: GameCommand): Promise<CommandAck> {
    const envelope = {
      commandId: createCommandId(),
      expectedVersion: Math.max(0, this.#snapshots.version),
      command,
    };
    const first = await this.emitCommand(envelope);
    if (first.status !== "conflict") {
      if (
        (first.status === "applied" || first.status === "duplicate") &&
        first.version > this.#snapshots.version
      )
        await this.sync();
      return first;
    }
    await this.sync();
    const retried = await this.emitCommand({
      ...envelope,
      expectedVersion: Math.max(0, this.#snapshots.version),
    });
    if (
      (retried.status === "applied" || retried.status === "duplicate") &&
      retried.version > this.#snapshots.version
    )
      await this.sync();
    return retried;
  }

  public sync(): Promise<void> {
    return this.#syncRequest.run(async (isCurrent) => {
      const ack = await acknowledgeWithin<SyncAck>(
        (acknowledge) => this.#socket.emit("game:sync", acknowledge),
        this.#acknowledgementTimeoutMs,
      );
      if (!isCurrent()) return;
      if (!ack) {
        this.notifyStatus("offline");
        return;
      }
      if (ack.status === "ok") {
        if (this.#snapshots.accept(ack.snapshot, true) !== "ignored") this.notifyStatus("online");
      } else {
        clearInvalidRecoveryCredential(this.#roomCode, ack.code);
        this.notifyStatus(ack.code === "ROOM_EXPIRED" ? "expired" : "invalid");
      }
    });
  }

  private async emitCommand(
    envelope: Parameters<ClientToServerEvents["game:command"]>[0],
  ): Promise<CommandAck> {
    const attempt = (): Promise<CommandAck | undefined> =>
      acknowledgeWithin(
        (acknowledge) => this.#socket.emit("game:command", envelope, acknowledge),
        this.#acknowledgementTimeoutMs,
      );
    return (
      (await attempt()) ??
      (await attempt()) ?? {
        status: "rejected",
        code: "PERSISTENCE_FAILED",
        message: "The server did not acknowledge the command. It is safe to retry.",
      }
    );
  }

  private notifyStatus(status: ConnectionStatus): void {
    if (status === "invalid" || status === "expired") this.#syncRequest.invalidate();
    this.#status.set(status);
  }
}

function createCommandId(): string {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === "function") return webCrypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (webCrypto?.getRandomValues) webCrypto.getRandomValues(bytes);
  else
    for (let index = 0; index < bytes.length; index += 1)
      bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
